import { AsyncSubject, forkJoin, retry, Subject } from "rxjs";
import { HelpDeskCategory, HelpDeskTicket } from "src/app/business/help-desk/help-desk-ticket";
import { TranslateUtilityService } from "src/app/system/utility/translation-utility.service";
import { MEAPIService } from "../../services/api/me-api.service";
import { BasicAsyncResponseSuccessHandler, MEAPIResponse, MEDoNothingResponseHandler, MEEntityAPIResponseHandler, ResponseCode } from "../../services/api/me-api-response";
import { MEError } from "../../error/me-error";
import { InternalSettingsService } from "../../services/internal-settings/internal-settings.service";
import { StringUtilityService } from "src/app/system/utility/string-utility.service";
import { ChatMessage } from "./chat-message";
import { ChatContextService } from "./chat-context.service";
import { ChatUtility } from "./chat-utility";
import { PlatformService } from "src/app/system/utility/platform.service";
import { WebSocketService } from "src/app/system/utility/websocket.service";
import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import { ChatDialogState } from "./chat.service";
import { Overlay } from "@angular/cdk/overlay";
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from "@angular/material/dialog";
import { ResponsiveDesignService, ScreenType } from "../../services/responsive-design/responsive-design.service";
import { ChatDialogComponent } from "./chat-dialog/chat-dialog.component";

export abstract class AbstractChatService {

  private readonly API_CHAT_HISTORY: string = MEAPIService.BASE_URL + '/customer/helpDeskTicket/chatHistory';
  private readonly API_CATEGORIES: string = MEAPIService.BASE_URL + '/customer/helpDeskCategory/namesList';

  private readonly WEBSOCKET_API: string = MEAPIService.BASE_URL + '/websocketendpoint';
  private readonly API_CHAT_SEND: string = MEAPIService.BASE_URL + '/customer/chatSendMessage';
  public categories$: AsyncSubject<HelpDeskCategory[]> = new AsyncSubject<HelpDeskCategory[]>();
  protected categories: HelpDeskCategory[] = [];
  protected dlgChat!: MatDialogRef<ChatDialogComponent, null>;

  private wsSubject!: WebSocketSubject<string>;
  public stateDialog$: Subject<ChatDialogState> = new Subject<ChatDialogState>();

  abstract handleSocketMessage(rawMsg: ChatMessage | string): void;
  protected abstract handleStateChange(newState: ChatDialogState): void;

  constructor(
    public srvApi: MEAPIService,
    protected translate: TranslateUtilityService,
    srvIs: InternalSettingsService,
    protected srvChatContext: ChatContextService,
    private srvWebSocket: WebSocketService,
    private srvDialog: MatDialog,
    private overlay: Overlay,
    protected srvRpDesign: ResponsiveDesignService,

  ) {

    // Retrieve Profile Info
    forkJoin([srvIs.getCurrentSettings()])
      .subscribe(([is]) => {
        srvChatContext.userName = is.profile.username;
        srvChatContext.alias = is.profile.alias;
        srvChatContext.displayName = StringUtilityService.getCustomerFullName(is);
      })


    // Load the Categories
    this.categories$.subscribe((categories) => {
      this.categories = categories;
    });


    // // Handle Dialog State Change
    this.stateDialog$.subscribe((newState: ChatDialogState) => {
      this.handleStateChange(newState);
    })

    // Handle Responsive Size
    this.srvRpDesign.screenType.subscribe((type: ScreenType) => {
      this.handleDialogSize(type);
    })

  }

  public instant(key: string | Array<string>, interpolateParams?: Object): any {
    return this.translate.instant(key, interpolateParams);
  }


  /**
   * Various API Service 
   */
  public loadCurrentChatHistory(item: HelpDeskTicket): AsyncSubject<boolean> {
    let ret: AsyncSubject<boolean> = new AsyncSubject<boolean>();

    if (item) {
      this.srvApi.Post(this.API_CHAT_HISTORY, { hdId: item.id }, [new ChatHistoryParser(this.srvChatContext, item)]).subscribe((r: boolean) => {
        ret.next(true);
        ret.complete();
      })
    } else {
      ret.next(true);
      ret.complete();
    }

    return ret;
  }

  public loadCategories(): AsyncSubject<boolean> {
    if (this.categories.length > 0) {
      let ret = new AsyncSubject<boolean>();
      ret.next(true);
      ret.complete();
      return ret;
    }
    return this.srvApi.Get<HelpDeskCategory[]>(this.API_CATEGORIES, [new BasicAsyncResponseSuccessHandler(this.categories$)]);
  }

  protected postChatAttachment(imgMsg: ChatMessage): AsyncSubject<boolean> {
    let message: ChatMessage = {
      channel: "hd",
      imageData: imgMsg.imageData!.replace('data:', '').replace(/^.+,/, ''),
      imageName: imgMsg.imageName,
      pm: imgMsg.pm,
      helpDeskTicketId: imgMsg.helpDeskTicketId,
      localId: imgMsg.localId
    }

    let bean = { message: ChatUtility.serializeSocketMessage(message) };
    return this.srvApi.Post(this.API_CHAT_SEND, bean, [new MEDoNothingResponseHandler(ResponseCode.SUCCESS)]);
  }

  /**
 * Open the Dialog if it was closed before
 */
  protected reOpenDialog() {
    if (!this.dlgChat || (this.dlgChat.getState() == MatDialogState.CLOSED)) {
      this.dlgChat = this.srvDialog.open(ChatDialogComponent, this.buildOptions());
    }
  }

  public connect() {

    // Mobile call Cordorva Websocket
    if (PlatformService.isMobile()) {
      this.srvWebSocket.connect();
      return;
    };

    // Browser call RxJS Websocket
    if (!this.wsSubject || this.wsSubject.closed) {
      this.wsSubject = webSocket<string>({
        url: this.getWebSocketURL(),
        deserializer: (e) => e.data,
        serializer: (e) => e
      });

      this.wsSubject
        .pipe(retry({ delay: 3000 }))
        .subscribe((rawMsg: string) => {
          this.handleSocketMessage(rawMsg);
        })
    }
  }

  protected sendSocketMessage(str: string) {
    if (PlatformService.isMobile()) {
      this.srvWebSocket.send(str);
    }
    else {
      this.wsSubject.next(str);
    }
  }

  private getWebSocketURL(): string {
    let port = window.location.port ? ':' + window.location.port : '';
    let url = window.location.protocol + '//' + window.location.hostname + port + this.WEBSOCKET_API;
    return url.replace("https://", "wss://").replace("http://", "ws://");
  }


  protected buildOptions(): MatDialogConfig {
    let dimension = this.getDialogDimensions();
    let options = {
      ariaModal: false,
      hasBackdrop: false,
      disableClose: true,
      position: dimension.position,
      autoFocus: false,
      maxWidth: dimension.width,
      // data: { state$: this.stateDialog },
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    };

    return options;
  }


  protected getDialogDimensions() {
    let smallSize = { width: '500px', position: { bottom: '100px', right: '25px' } };
    let largeSize = { width: '500px', position: { bottom: '50px', right: '50px' } };

    let dimension;
    switch (this.srvRpDesign.screenType.value) {
      case ScreenType.TypeXS:
      case ScreenType.TypeS:
        dimension = smallSize;
        break;
      case ScreenType.TypeM:
      case ScreenType.TypeL:
      case ScreenType.TypeXL:
      default:
        dimension = largeSize;
        break;
    }
    return dimension;
  }


  protected handleDialogSize(type: ScreenType) {
    if (this.srvRpDesign.isMobile$.value) return;

    if (this.dlgChat) {
      let dimension = this.getDialogDimensions();
      this.dlgChat.updatePosition(dimension.position);
    }
  }

  public getChatDialogTitle(): string {
    let ticket = this.srvChatContext.getChatTicket();
    if (!ticket) return '';
    let categoryName = this.getCategoryName(ticket.categoryId);
    let result = ticket.id + ((categoryName) ? ' - ' + categoryName : '');
    return result;
  }

  public getCategoryName(catId: number): string {
    let ret = this.categories.find(c => c.id == catId);
    return ret?.name || '';
  }
  
}

class ChatHistoryParser implements MEEntityAPIResponseHandler<string[]> {
  public code: ResponseCode = ResponseCode.SUCCESS;
  constructor(private srvChatContext: ChatContextService, private ticket: HelpDeskTicket) { }

  handle(response: MEAPIResponse<string[]>): boolean | MEError {
    this.srvChatContext.parseChatResponse(this.ticket, response.entity.reverse()); // Reverse the order of the messages
    return true;
  }

}
